import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { RoleApiService } from '../services/role-api.service';
import { RoleDrawerService } from '../services/role-drawer.service';
import { RoleDetail } from '../models/role.model';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';

@Component({
  selector: 'app-role-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    HasPermissionDirective,
  ],
  templateUrl: './role-list.component.html',
})
export class RoleListComponent implements OnInit, OnDestroy {
  roles: RoleDetail[] = [];
  filtered: RoleDetail[] = [];
  loading = false;
  error = '';

  filtroSearch = '';
  filtroTipo: '' | 'system' | 'custom' = '';

  /** Total de módulos del catálogo (denominador del resumen de permisos). */
  totalModules = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: RoleApiService,
    private drawerService: RoleDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    // Total de módulos del catálogo: el resumen se muestra como "X de Y módulos"
    // para que se vea de un vistazo que cada rol tiene un alcance distinto
    // (INC_CRM_024 inc01). Si falla, se cae al formato corto "X módulos".
    this.api.getModules().subscribe({
      next: (modules) => (this.totalModules = modules.length),
      error: () => (this.totalModules = 0),
    });
    this.api.getManagedRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar los roles';
        this.loading = false;
      },
    });
  }

  aplicarFiltros(): void {
    let data = [...this.roles];
    if (this.filtroSearch.trim()) {
      const term = this.filtroSearch.trim().toLowerCase();
      data = data.filter(
        (r) =>
          r.name.toLowerCase().includes(term) ||
          (r.description ?? '').toLowerCase().includes(term)
      );
    }
    if (this.filtroTipo === 'system') data = data.filter((r) => r.isSystem);
    if (this.filtroTipo === 'custom') data = data.filter((r) => !r.isSystem);
    this.filtered = data.sort((a, b) => a.name.localeCompare(b.name));
  }

  /** Resumen de permisos: cuántos módulos con al menos "ver" tiene el rol. */
  permissionsSummary(role: RoleDetail): string {
    const total = role.permissions.filter((p) => p.canView).length;
    if (total === 0) return 'Sin permisos';
    if (this.totalModules > 0) return `${total} de ${this.totalModules} módulos`;
    return `${total} módulo${total === 1 ? '' : 's'}`;
  }

  /** Acciones concedidas al rol, para que la diferencia se lea sin abrir el detalle. */
  actionsSummary(role: RoleDetail): string {
    const acciones: string[] = [];
    if (role.permissions.some((p) => p.canCreate)) acciones.push('crear');
    if (role.permissions.some((p) => p.canEdit)) acciones.push('editar');
    if (role.permissions.some((p) => p.canDelete)) acciones.push('borrar');
    if (role.permissions.some((p) => p.canImport)) acciones.push('importar');
    if (role.permissions.some((p) => p.canExport)) acciones.push('exportar');
    return acciones.length ? `Ver, ${acciones.join(', ')}` : 'Solo ver';
  }

  nuevo(): void {
    this.drawerService.open();
  }

  ver(role: RoleDetail): void {
    this.drawerService.open({ roleId: role.id, readOnly: true });
  }

  editar(role: RoleDetail): void {
    if (role.isSystem) return;
    this.drawerService.open({ roleId: role.id });
  }

  async eliminar(role: RoleDetail): Promise<void> {
    if (role.isSystem) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar rol',
      message: `¿Eliminar el rol "${role.name}"? Esta acción no se puede deshacer.`,
    });
    if (!ok) return;
    this.api.deleteRole(role.id).subscribe({
      next: () => {
        this.cargar();
        this.toast.deleted('Rol', `Se eliminó "${role.name}" correctamente.`);
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar el rol',
        });
      },
    });
  }

  trackById = (_: number, role: RoleDetail) => role.id;
}
